<template>
  <div class="row">
    <el-row type="flex" class="row-bg" justify="space-around">
      <el-col :span="21">
        <div class="grid-content bg-purple">
          <h1 style="margin: 0;">
            Delegations
          </h1>
        </div>
      </el-col>
    </el-row>

    <div class="col-md-12 card">
      <div class="card-body row">
        <!-- Per Page Selector -->
        <div class="col-sm-3">
          <el-select
            class="select-default"
            v-model="pagination.perPage"
            placeholder="Per page"
          >
            <el-option
              class="select-default"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </div>

        <!-- Search Bar -->
        <div class="col-sm-9">
          <div class="pull-right">
            <fg-input
              class="input-sm"
              placeholder="Search"
              @keyup.enter="getDelegations"
              v-model="searchQuery"
              addon-right-icon="nc-icon nc-zoom-split"
            />
          </div>
        </div>

        <!-- Delegation Table -->
        <div class="col-sm-12 mt-2">
          <el-table
            class="table-striped"
            :data="pagedData"
            border
            style="width: 100%"
          >
            <!-- ID Column -->
            <el-table-column
              prop="id"
              label="ID"
              min-width="75"
            />

            <!-- Root User Column (Name and Email) -->
            <el-table-column
              label="Root User"
              min-width="250"
            >
              <template slot-scope="scope">
                {{ scope.row.root_user_name }} [{{ scope.row.root_user_email }}]
              </template>
            </el-table-column>

            <!-- Delegate Column (Name, Email, Role @ Location) -->
            <el-table-column
              label="Delegate"
              min-width="300"
            >
              <template slot-scope="scope">
                {{ scope.row.delegated_user_name }} [{{ scope.row.delegated_user_email }}]<br>
                {{ scope.row.role_name }} @ {{ scope.row.location_name }}
              </template>
            </el-table-column>

            <!-- Expiration Column -->
            <el-table-column
              label="Expiration"
              min-width="150"
            >
              <template slot-scope="scope">
                {{ formatDate(scope.row.expires_at) }}
              </template>
            </el-table-column>

            <!-- Actions Column (Edit/Delete) -->
            <el-table-column
              label="Actions"
              min-width="100"
              class-name="td-actions text-center"
            >
              <template slot-scope="props">
                <p-button
                  type="success"
                  size="sm"
                  icon
                  @click="openEditModal(props.row)"
                >
                  <i class="fa fa-edit" />
                </p-button>
                <p-button
                  type="danger"
                  size="sm"
                  icon
                  @click="openDeleteModal(props.row)"
                >
                  <i class="fa fa-trash" />
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Pagination Info -->
        <div class="col-sm-6 pagination-info">
          <p class="category">
            Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
          </p>
        </div>

        <!-- Pagination Controls -->
        <div class="col-sm-6">
          <p-pagination
            class="pull-right"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="pagination.total"
            @input="handlePageChanged"
          />
        </div>
      </div>
    </div>
    <!-- Edit Modal -->
    <el-dialog
      :title="'Edit Delegation for ' + (currentDelegation ? (currentDelegation.root_user_name) : '')"

      :visible.sync="editModalVisible"
      width="30%"
    >
      <el-form :model="currentDelegation">
        <el-form-item label="Expiration Date">
          <el-date-picker
            v-model="currentDelegation.expires_at"
            type="date"
            placeholder="Pick a date"
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="editModalVisible = false">
          Cancel
        </el-button>
        <el-button type="primary" @click="saveEdit">
          Save
        </el-button>
      </div>
    </el-dialog>

    <!-- Delete Modal -->
    <el-dialog
      title="Delete Delegation"
      :visible.sync="deleteModalVisible"
      width="30%"
    >
      <h4>Are you sure you want to delete this delegation?</h4>

      <span><b>*Deleting the delegation will also remove the person that was delegated to from the system.
        Only delete the delegation if a fake user had to be created when this delegation was created.
        For delegations that link two users that should exist,
        just set the expiration date to today so that the delegation will end, but the user will not be deleted.
      </b></span>

      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteModalVisible = false">
          Cancel
        </el-button>
        <el-button type="danger" @click="confirmDelete">
          Delete
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import {
    mapState
} from 'vuex';
import {
    Table, TableColumn, Select, Option, Dialog, Form, FormItem, DatePicker, Button
} from 'element-ui';
import Pagination from '../../components/UIComponents/Pagination';
import { RepositoryFactory } from '../../repositories/repositoryFactory';
import apiService from '../../repositories/apiService';

const DelegationsRepository = RepositoryFactory.get('delegations');
const LocationsRepository = RepositoryFactory.get('locations');
const RolesRepository = RepositoryFactory.get('roles');
const data = 'Delegations';

Vue.use(Table);
Vue.use(TableColumn);
Vue.use(Select);
Vue.use(Option);
Vue.use(Dialog);
Vue.use(Form);
Vue.use(FormItem);
Vue.use(DatePicker);
Vue.use(Button);

export default {
    components: {
        [Pagination.name]: Pagination
    },
    computed: {
        pagedData() {
            return this.tableData;
        },
        to() {
            let highBound = this.from + this.pagination.perPage;
            if (this.total < highBound) {
                highBound = this.total;
            }
            return highBound;
        },
        from() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            return this.pagination.total;
        },
        ...mapState({
            roles: (state) => state.People.roles
        })
    },
    data() {
        return {
            pagination: {
                perPage: 20,
                currentPage: 1,
                perPageOptions: [20, 50, 100],
                total: 0
            },
            error: '',
            searchQuery: '',
            tableData: [],
            users: [],
            locations: [],
            currentDelegation: {}, // Holds the selected delegation for edit
            editModalVisible: false,
            deleteModalVisible: false,
        };
    },
    methods: {
        openEditModal(row) {
            this.currentDelegation = { ...row }; // Copy the selected row to currentDelegation
            this.editModalVisible = true; // Show the edit modal
        },
        saveEdit() {
            // Implement the logic to save the updated expiration date
            DelegationsRepository.updateDelegation(this.currentDelegation)
                .then((response) => {
                    this.editModalVisible = false; // Close modal on success
                    this.$message(response.data.message);
                    this.getDelegations(); // Refresh the delegations
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                });
        },
        openDeleteModal(row) {
            this.currentDelegation = { ...row }; // Copy the selected row for deletion
            this.deleteModalVisible = true; // Show the delete modal
        },
        confirmDelete() {
            // Implement the logic to delete the delegation
            DelegationsRepository.deleteDelegation(this.currentDelegation.id)
                .then((response) => {
                    this.deleteModalVisible = false; // Close modal on success
                    this.$message(response.data.message);
                    this.getDelegations(); // Refresh the delegations
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                });
        },
        getDelegations() {
            DelegationsRepository.getDelegations(this.pagination.currentPage, this.pagination.perPage, this.searchQuery)
                .then((response) => {
                    this.tableData = response.data.data;
                    this.pagination.total = response.data.total;
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                });
        },
        handlePageChanged(newPage) {
            this.pagination.currentPage = newPage;
            this.getDelegations();
        },
        formatDate(dateString) {
            if (!dateString) {
                return '';
            }
            const date = new Date(dateString);
            const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        }
    },
    created() {
        this.getDelegations();
    }
};
</script>

  <style scoped>
  .el-table .td-actions {
    button.btn {
      margin-right: 5px;
    }
  }
  </style>
